(() => {
  if (window.__taylorsoccerFigmaProductGalleryLoaded) return;
  window.__taylorsoccerFigmaProductGalleryLoaded = true;

  const desktopQuery = window.matchMedia('(min-width: 1000px)');
  const activeClass = 'is-active';
  const primaryClass = 'is-primary';

  class FigmaProductGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.section = gallery.closest('[data-section-type="product"]');
      this.desktopStack = gallery.querySelector('[data-figma-desktop-stack]');
      this.thumbnailList = gallery.querySelector('.product-gallery__thumbnail-list');
      this.thumbnails = Array.from(gallery.querySelectorAll('.product-gallery__thumbnail'));
      this.mediaItems = Array.from(gallery.querySelectorAll('[data-figma-desktop-media]'));
      this.nativeMediaItems = Array.from(gallery.querySelectorAll('.product-gallery__carousel-item'));
      this.onClick = this.onClick.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      if (!this.desktopStack || !this.mediaItems.length) return;

      this.gallery.addEventListener('click', this.onClick);
      this.section?.addEventListener('variant:changed', this.onVariantChanged);
      desktopQuery.addEventListener?.('change', this.onViewportChange);
      this.sync();
      requestAnimationFrame(() => this.sync());
    }

    destroy() {
      this.gallery.removeEventListener('click', this.onClick);
      this.section?.removeEventListener('variant:changed', this.onVariantChanged);
      desktopQuery.removeEventListener?.('change', this.onViewportChange);
      this.reset();
    }

    get availableMedia() {
      const availableIds = new Set(
        this.thumbnails
          .filter((thumbnail) => !thumbnail.classList.contains('is-filtered'))
          .map((thumbnail) => thumbnail.dataset.mediaId)
      );

      if (!availableIds.size) return this.mediaItems;
      return this.mediaItems.filter((item) => availableIds.has(item.dataset.mediaId));
    }

    get currentMediaId() {
      const selectedThumbnail = this.thumbnails.find(
        (thumbnail) => thumbnail.classList.contains('is-nav-selected') && !thumbnail.classList.contains('is-filtered')
      );

      if (selectedThumbnail) return selectedThumbnail.dataset.mediaId;

      const selectedNativeMedia = this.nativeMediaItems.find(
        (item) => item.classList.contains('is-selected') && !item.classList.contains('is-filtered')
      );

      if (selectedNativeMedia) return selectedNativeMedia.dataset.mediaId;

      const currentPrimary = this.mediaItems.find((item) => item.classList.contains(primaryClass));
      return currentPrimary?.dataset.mediaId || this.availableMedia[0]?.dataset.mediaId;
    }

    reset() {
      this.mediaItems.forEach((item) => {
        item.classList.remove(activeClass, primaryClass);
        item.style.order = '';
      });
    }

    prepareMedia(item, isPrimary) {
      item.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        if (isPrimary) image.fetchPriority = 'high';
      });
    }

    setThumbnail(mediaId, behavior = 'auto') {
      const id = String(mediaId || '');
      let selectedThumbnail = null;

      this.thumbnails.forEach((thumbnail) => {
        const selected = thumbnail.dataset.mediaId === id;
        thumbnail.classList.toggle('is-nav-selected', selected);
        if (selected) selectedThumbnail = thumbnail;
      });

      if (!selectedThumbnail || !this.thumbnailList) return;

      const top = selectedThumbnail.offsetTop - (this.thumbnailList.clientHeight - selectedThumbnail.clientHeight) / 2;
      this.thumbnailList.scrollTo({ top: Math.max(0, top), behavior });
    }

    setPair(mediaId, behavior = 'auto') {
      if (!desktopQuery.matches) {
        this.reset();
        return;
      }

      const available = this.availableMedia;
      if (!available.length) return;

      const id = String(mediaId || '');
      let primaryIndex = available.findIndex((item) => item.dataset.mediaId === id);
      if (primaryIndex < 0) primaryIndex = 0;

      const primary = available[primaryIndex];
      const secondary = available[primaryIndex + 1] || available[primaryIndex - 1] || null;

      this.reset();
      primary.classList.add(activeClass, primaryClass);
      primary.style.order = '0';
      this.prepareMedia(primary, true);

      if (secondary && secondary !== primary) {
        secondary.classList.add(activeClass);
        secondary.style.order = '1';
        this.prepareMedia(secondary, false);
      }

      this.setThumbnail(primary.dataset.mediaId, behavior);
    }

    sync(behavior = 'auto') {
      if (!desktopQuery.matches) {
        this.reset();
        return;
      }

      this.setPair(this.currentMediaId, behavior);
    }

    queueSync(mediaId, behavior = 'auto') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setPair(mediaId || this.currentMediaId, behavior);
        });
      });
    }

    onClick(event) {
      if (!desktopQuery.matches) return;
      const thumbnail = event.target.closest('.product-gallery__thumbnail');
      if (!thumbnail || !this.gallery.contains(thumbnail) || thumbnail.classList.contains('is-filtered')) return;
      this.setPair(thumbnail.dataset.mediaId, 'smooth');
    }

    onVariantChanged(event) {
      if (!desktopQuery.matches) return;
      this.queueSync(event.detail?.variant?.featured_media?.id, 'auto');
    }

    onViewportChange() {
      if (desktopQuery.matches) {
        this.sync();
      } else {
        this.reset();
      }
    }
  }

  const instances = new WeakMap();

  const init = (root = document) => {
    root.querySelectorAll?.('.product-gallery--figma-stacked').forEach((gallery) => {
      if (!instances.has(gallery)) instances.set(gallery, new FigmaProductGallery(gallery));
    });
  };

  const destroy = (root) => {
    root.querySelectorAll?.('.product-gallery--figma-stacked').forEach((gallery) => {
      instances.get(gallery)?.destroy();
      instances.delete(gallery);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
